import React, { useState } from 'react';
import {
  Building,
  Users,
  DollarSign,
  Calendar,
  Search,
  Filter,
  Eye,
  Download,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  FileText,
  Plus,
  Activity
} from 'lucide-react';
import { format, addDays, subDays } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import StatCard from '../../components/StatCard';

interface Facility {
  id: string;
  name: string;
  type: 'aged_care' | 'ndis' | 'hospital' | 'clinic';
  contactPerson: string;
  email: string;
  phone: string;
  address: string;
  totalPatients: number;
  monthlySpend: number;
  lastInvoice: string;
  status: 'active' | 'suspended' | 'pending';
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  facilityId: string;
  facilityName: string;
  billingPeriod: string;
  totalAmount: number;
  status: 'draft' | 'sent' | 'paid' | 'overdue';
  dueDate: string;
  createdDate: string;
  items: InvoiceItem[];
}

interface InvoiceItem {
  id: string;
  patientName: string;
  patientId: string;
  medication: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  serviceType: 'dispensing' | 'delivery' | 'consultation' | 'packaging';
}

const FacilityBilling: React.FC = () => {
  const { user } = useAuth();
  const [selectedFacility, setSelectedFacility] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);

  const mockFacilities: Facility[] = [
    {
      id: 'fac-001',
      name: 'Sunnydale Aged Care',
      type: 'aged_care',
      contactPerson: 'Margaret Wilson',
      email: 'billing@sunnydale.com.au',
      phone: '(02) 9876 5432',
      address: '123 Care Street, Sydney NSW 2000',
      totalPatients: 45,
      monthlySpend: 12500.50,
      lastInvoice: format(subDays(new Date(), 5), 'yyyy-MM-dd'),
      status: 'active'
    },
    {
      id: 'fac-002',
      name: 'NDIS Support Services',
      type: 'ndis',
      contactPerson: 'David Chen',
      email: 'accounts@ndis-support.com.au',
      phone: '(02) 8765 4321',
      address: '456 Support Ave, Melbourne VIC 3000',
      totalPatients: 28,
      monthlySpend: 8750.25,
      lastInvoice: format(subDays(new Date(), 3), 'yyyy-MM-dd'),
      status: 'active'
    },
    {
      id: 'fac-003',
      name: 'Royal Hospital',
      type: 'hospital',
      contactPerson: 'Dr. Sarah Johnson',
      email: 'pharmacy@royalhospital.com.au',
      phone: '(02) 7654 3210',
      address: '789 Medical Drive, Brisbane QLD 4000',
      totalPatients: 120,
      monthlySpend: 25000.00,
      lastInvoice: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
      status: 'active'
    }
  ];

  const mockInvoices: Invoice[] = [
    {
      id: 'inv-001',
      invoiceNumber: 'INV-2024-001',
      facilityId: 'fac-001',
      facilityName: 'Sunnydale Aged Care',
      billingPeriod: 'December 2024',
      totalAmount: 12500.50,
      status: 'sent',
      dueDate: format(addDays(new Date(), 15), 'yyyy-MM-dd'),
      createdDate: format(subDays(new Date(), 5), 'yyyy-MM-dd'),
      items: []
    },
    {
      id: 'inv-002',
      invoiceNumber: 'INV-2024-002',
      facilityId: 'fac-002',
      facilityName: 'NDIS Support Services',
      billingPeriod: 'December 2024',
      totalAmount: 8750.25,
      status: 'paid',
      dueDate: format(subDays(new Date(), 10), 'yyyy-MM-dd'),
      createdDate: format(subDays(new Date(), 20), 'yyyy-MM-dd'),
      items: []
    }
  ];

  const filteredFacilities = mockFacilities.filter(facility => {
    const matchesSearch = facility.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         facility.contactPerson.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || facility.type === filterType;
    const matchesStatus = filterStatus === 'all' || facility.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'aged_care': return 'bg-blue-100 text-blue-800';
      case 'ndis': return 'bg-green-100 text-green-800';
      case 'hospital': return 'bg-purple-100 text-purple-800';
      case 'clinic': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getInvoiceStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'paid': return 'bg-green-100 text-green-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleCreateInvoice = (facilityId: string) => {
    console.log(`Creating invoice for facility ${facilityId}`);
  };

  const handleSendInvoice = (invoiceId: string) => {
    console.log(`Sending invoice ${invoiceId}`);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
            Facility Billing 🏥
          </h1>
          <p className="text-gray-600 mt-2">Manage aged care, NDIS, and hospital billing for {user?.branch}.</p>
        </div>
        <div className="flex space-x-3">
          <button 
            onClick={() => setShowCreateInvoice(true)}
            className="group bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-xl hover:from-purple-700 hover:to-pink-700 hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center space-x-2"
          >
            <Plus className="h-4 w-4 group-hover:rotate-90 transition-transform duration-200" />
            <span>Create Invoice</span>
          </button>
          <button className="group bg-white/80 backdrop-blur-md text-gray-700 px-6 py-3 rounded-xl hover:bg-white hover:shadow-lg transition-all duration-300 hover:scale-105 flex items-center space-x-2 border border-gray-200">
            <Download className="h-4 w-4 group-hover:scale-110 transition-transform duration-200" />
            <span>Export Data</span>
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Active Facilities"
          value={mockFacilities.filter(f => f.status === 'active').length}
          subtitle="Currently active"
          icon={<Building className="h-6 w-6" />}
          color="text-blue-600"
          bgGradient="bg-gradient-to-br from-blue-500 to-blue-600"
        />
        <StatCard
          title="Total Patients"
          value={mockFacilities.reduce((sum, f) => sum + f.totalPatients, 0)}
          subtitle="Across all facilities"
          icon={<Users className="h-6 w-6" />}
          color="text-green-600"
          bgGradient="bg-gradient-to-br from-green-500 to-green-600"
        />
        <StatCard
          title="Monthly Revenue"
          value={`$${mockFacilities.reduce((sum, f) => sum + f.monthlySpend, 0).toLocaleString()}`}
          subtitle="This month"
          icon={<DollarSign className="h-6 w-6" />}
          color="text-purple-600"
          bgGradient="bg-gradient-to-br from-purple-500 to-purple-600"
        />
        <StatCard
          title="Pending Invoices"
          value={mockInvoices.filter(i => i.status === 'sent' || i.status === 'draft').length}
          subtitle="Awaiting payment"
          icon={<FileText className="h-6 w-6" />}
          color="text-orange-600"
          bgGradient="bg-gradient-to-br from-orange-500 to-orange-600"
        />
      </div>

      {/* Filters */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search facilities, contacts..."
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Types</option>
            <option value="aged_care">Aged Care</option>
            <option value="ndis">NDIS</option>
            <option value="hospital">Hospital</option>
            <option value="clinic">Clinic</option>
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="pending">Pending</option>
          </select>

          <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center space-x-2">
            <Filter className="h-4 w-4" />
            <span>Apply Filters</span>
          </button>
        </div>
      </div>

      {/* Facilities and Invoices */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Facilities List */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Facilities</h3>
          <div className="space-y-4">
            {filteredFacilities.map((facility) => (
              <div 
                key={facility.id} 
                className={`p-4 rounded-xl border cursor-pointer transition-all hover:shadow-md ${
                  selectedFacility === facility.id ? 'border-purple-300 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedFacility(facility.id)}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                      <Building className="h-4 w-4 text-gray-600" />
                      <span className="font-semibold text-gray-900">{facility.name}</span>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(facility.type)}`}>
                      {facility.type.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(facility.status)}`}>
                    {facility.status.toUpperCase()}
                  </span>
                </div>
                
                <div className="mb-3">
                  <p className="font-medium text-gray-900">{facility.contactPerson}</p>
                  <p className="text-sm text-gray-600">{facility.email}</p>
                  <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                    <span className="flex items-center space-x-1">
                      <Users className="h-4 w-4" />
                      <span>{facility.totalPatients} patients</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <DollarSign className="h-4 w-4" />
                      <span>${facility.monthlySpend.toLocaleString()}/month</span>
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Last invoice: {format(new Date(facility.lastInvoice), 'dd/MM/yyyy')}
                  </div>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCreateInvoice(facility.id);
                    }}
                    className="bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700"
                  >
                    Create Invoice
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Invoices */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Recent Invoices</h3>
          <div className="space-y-4">
            {mockInvoices.map((invoice) => (
              <div key={invoice.id} className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <FileText className="h-4 w-4 text-gray-600" />
                    <span className="font-semibold text-gray-900">{invoice.invoiceNumber}</span>
                  </div>
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${getInvoiceStatusColor(invoice.status)}`}>
                    {invoice.status.toUpperCase()}
                  </span>
                </div>
                
                <div className="mb-3">
                  <p className="font-medium text-gray-900">{invoice.facilityName}</p>
                  <p className="text-sm text-gray-600">{invoice.billingPeriod}</p>
                  <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                    <span className="flex items-center space-x-1">
                      <DollarSign className="h-4 w-4" />
                      <span>${invoice.totalAmount.toLocaleString()}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Calendar className="h-4 w-4" />
                      <span>Due: {format(new Date(invoice.dueDate), 'dd/MM/yyyy')}</span>
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Created: {format(new Date(invoice.createdDate), 'dd/MM/yyyy')}
                  </div>
                  <div className="flex space-x-2">
                    <button className="bg-gray-100 text-gray-700 px-3 py-1 rounded text-sm hover:bg-gray-200">
                      <Eye className="h-3 w-3" />
                    </button>
                    {invoice.status === 'draft' && (
                      <button 
                        onClick={() => handleSendInvoice(invoice.id)}
                        className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                      >
                        Send
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacilityBilling;
